import { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { uid } from "@/lib/uid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, Camera, Trash2, Plus, CheckCircle2, AlertCircle, Clock, StickyNote } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import AttrezzaturaCard from "./AttrezzaturaCard";

// Concorrenza per l'analisi batch (evita di sovraccaricare Dual-AI)
const BATCH_CONCURRENCY = 2;

async function runWithConcurrency(items, worker, limit) {
  const queue = items.map((it, idx) => ({ it, idx }));
  const runners = Array(Math.min(limit, queue.length))
    .fill(0)
    .map(async () => {
      while (queue.length) {
        const next = queue.shift();
        if (!next) return;
        await worker(next.it, next.idx);
      }
    });
  await Promise.all(runners);
}

function QueueItemRow({ q, onRemove, disabled }) {
  const statusIcon =
    q.status === "analyzing" ? (
      <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
    ) : q.status === "done" ? (
      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
    ) : q.status === "error" ? (
      <AlertCircle className="w-4 h-4 text-rose-600" />
    ) : (
      <Clock className="w-4 h-4 text-muted-foreground" />
    );
  const statusLabel =
    q.status === "analyzing"
      ? "Analisi in corso…"
      : q.status === "done"
      ? "Completata"
      : q.status === "error"
      ? q.error || "Errore"
      : "In attesa";
  return (
    <div
      className="flex items-center gap-3 p-2 border border-border rounded bg-card"
      data-testid={`s6-queue-item-${q._qid}`}
    >
      {q.thumbUrl ? (
        <img src={q.thumbUrl} alt="" className="w-14 h-10 object-cover rounded border border-border" />
      ) : (
        <div className="w-14 h-10 bg-muted rounded flex items-center justify-center">
          <Camera className="w-4 h-4 text-muted-foreground" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{q.label || q.macchinaName || "Attrezzatura"}</div>
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          {statusIcon} {statusLabel}
          {q.targhettaName && <span className="ml-2 italic">+ targhetta</span>}
        </div>
      </div>
      {!disabled && q.status !== "analyzing" && (
        <button
          onClick={onRemove}
          className="text-muted-foreground hover:text-destructive p-1"
          data-testid={`s6-queue-remove-${q._qid}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export default function StepAttrezzature({ data, onChange, aiLoading, setAiLoading }) {
  const list = Array.isArray(data) ? data : [];
  const fileRef = useRef(null);
  const plateRef = useRef(null);
  const [pending, setPending] = useState({ macchina: null, targhetta: null, label: "", note: "" });
  const [queue, setQueue] = useState([]); // [{ _qid, label, note, macchina:File, targhetta:File|null, thumbUrl, status, error }]

  const addToQueue = () => {
    if (!pending.macchina) {
      toast.error("Carica una foto della macchina prima di aggiungere alla coda");
      return;
    }
    const thumbUrl = URL.createObjectURL(pending.macchina);
    setQueue((q) => [
      ...q,
      {
        _qid: uid(),
        label: pending.label?.trim() || "",
        note: pending.note?.trim() || "",
        macchina: pending.macchina,
        targhetta: pending.targhetta,
        macchinaName: pending.macchina.name,
        targhettaName: pending.targhetta?.name || "",
        thumbUrl,
        status: "idle",
        error: null,
      },
    ]);
    setPending({ macchina: null, targhetta: null, label: "", note: "" });
    if (fileRef.current) fileRef.current.value = "";
    if (plateRef.current) plateRef.current.value = "";
  };

  const removeFromQueue = (qid) => {
    setQueue((q) => {
      const it = q.find((x) => x._qid === qid);
      if (it?.thumbUrl) URL.revokeObjectURL(it.thumbUrl);
      return q.filter((x) => x._qid !== qid);
    });
  };

  const updateQueueItem = (qid, patch) => {
    setQueue((q) => q.map((x) => (x._qid === qid ? { ...x, ...patch } : x)));
  };

  const analyzeQueue = async () => {
    const toProcess = queue.filter((q) => q.status !== "done");
    if (toProcess.length === 0) {
      toast.error("La coda è vuota");
      return;
    }
    setAiLoading(true);
    const successful = [];

    await runWithConcurrency(
      toProcess,
      async (q) => {
        updateQueueItem(q._qid, { status: "analyzing", error: null });
        const fd = new FormData();
        fd.append("file", q.macchina);
        if (q.targhetta) fd.append("targhetta", q.targhetta);
        if (q.note) fd.append("note", q.note);
        try {
          const r = await api.post("/dvr/ai/machine", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 300000,
          });
          const result = { ...r.data, _uid: uid() };
          if (q.label) {
            result.riconoscimento = { ...(result.riconoscimento || {}), tipo_macchina: q.label };
          }
          if (q.note) {
            result._operator_note = q.note;
          }
          successful.push({ qid: q._qid, result });
          updateQueueItem(q._qid, { status: "done" });
        } catch (err) {
          const msg = err.response?.data?.detail || err.message || "AI error";
          updateQueueItem(q._qid, { status: "error", error: msg });
        }
      },
      BATCH_CONCURRENCY
    );

    if (successful.length > 0) {
      // Append tutti in un unico onChange per evitare overwrite dovuti a chiusure obsolete
      onChange([...list, ...successful.map((s) => s.result)]);
      // Rimuovi dalla coda gli item completati con successo
      setQueue((q) => {
        const doneIds = new Set(successful.map((s) => s.qid));
        return q.filter((x) => {
          if (doneIds.has(x._qid)) {
            if (x.thumbUrl) URL.revokeObjectURL(x.thumbUrl);
            return false;
          }
          return true;
        });
      });
      toast.success(`${successful.length} attrezzatur${successful.length === 1 ? "a analizzata" : "e analizzate"}`);
    }
    const failed = toProcess.length - successful.length;
    if (failed > 0) {
      toast.error(`${failed} attrezzatur${failed === 1 ? "a fallita" : "e fallite"} — riprova`);
    }
    setAiLoading(false);
  };

  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const updateItem = (i, next) => onChange(list.map((it, idx) => (idx === i ? next : it)));

  const queuePending = queue.filter((q) => q.status !== "done").length;

  return (
    <div className="space-y-4" data-testid="step-attrezzature">
      <p className="text-sm text-muted-foreground">
        Carica <b>più attrezzature in coda</b> e poi lancia un'unica <b>analisi Dual-AI</b> (Gemini 3 Pro → GPT-5.5)
        per valutarle in batch. Ogni risultato è <b>modificabile manualmente</b> (rischi, DPI, misure, procedura,
        manutenzione). Rif: Dir. Macchine 2006/42/CE, All. V/VII D.Lgs. 81/08.
      </p>

      {/* Uploader singolo → aggiungi alla coda */}
      <div className="border border-border rounded-md p-3 bg-muted/20 space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">1) Aggiungi attrezzatura alla coda</div>
        <div>
          <Label className="text-xs">Etichetta / nome breve (opzionale)</Label>
          <Input
            placeholder="Es. Trapano a colonna reparto 1"
            value={pending.label}
            onChange={(e) => setPending({ ...pending, label: e.target.value })}
            data-testid="s6-input-label"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Foto macchina / attrezzatura</Label>
            <button
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-border rounded-md p-4 text-sm hover:bg-muted/30 w-full mt-1"
              data-testid="s6-upload-machine"
              type="button"
            >
              <Camera className="w-5 h-5 mx-auto mb-1" />
              {pending.macchina ? pending.macchina.name.slice(0, 32) : "Scatta o carica foto"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => setPending({ ...pending, macchina: e.target.files?.[0] || null })}
            />
          </div>
          <div>
            <Label className="text-xs">Foto targhetta CE (opzionale)</Label>
            <button
              onClick={() => plateRef.current?.click()}
              className="border-2 border-dashed border-border rounded-md p-4 text-sm hover:bg-muted/30 w-full mt-1"
              data-testid="s6-upload-plate"
              type="button"
            >
              <Camera className="w-5 h-5 mx-auto mb-1" />
              {pending.targhetta ? pending.targhetta.name.slice(0, 32) : "Foto targhetta (opz.)"}
            </button>
            <input
              ref={plateRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => setPending({ ...pending, targhetta: e.target.files?.[0] || null })}
            />
          </div>
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1">
            <StickyNote className="w-3 h-3" /> Note operatore (modalità d'uso, condizioni sopralluogo, dettagli non visibili in foto)
          </Label>
          <Textarea
            rows={3}
            placeholder="Es. usata 4 h/giorno per taglio profilati; operatore singolo; rumore percepito elevato; manca cuffia protezione mola…"
            value={pending.note}
            onChange={(e) => setPending({ ...pending, note: e.target.value })}
            data-testid="s6-input-note"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Queste note vengono passate all'AI insieme alle foto per migliorare la valutazione.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={addToQueue}
            disabled={!pending.macchina || aiLoading}
            variant="secondary"
            className="rounded-full"
            data-testid="s6-add-queue"
          >
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi alla coda
          </Button>
        </div>
      </div>

      {/* Coda */}
      {queue.length > 0 && (
        <div className="border border-indigo-200 bg-indigo-50/40 rounded-md p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-indigo-800 font-semibold">
              2) Coda di analisi ({queue.length})
            </div>
            <Button
              onClick={analyzeQueue}
              disabled={aiLoading || queuePending === 0}
              className="rounded-full"
              data-testid="s6-analyze-batch"
            >
              {aiLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Analizza tutte con AI ({queuePending})
            </Button>
          </div>
          <div className="space-y-2">
            {queue.map((q) => (
              <QueueItemRow
                key={q._qid}
                q={q}
                disabled={aiLoading}
                onRemove={() => removeFromQueue(q._qid)}
              />
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Elaborazione in parallelo ({BATCH_CONCURRENCY} contemporanee). Puoi continuare ad aggiungere attrezzature
            anche durante l'analisi.
          </p>
        </div>
      )}

      {/* Attrezzature analizzate */}
      <div className="space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
          3) Attrezzature analizzate ({list.length})
        </div>
        {list.map((a, i) => (
          <AttrezzaturaCard
            key={a._uid || `attr-${i}`}
            item={a}
            index={i}
            onSave={(next) => updateItem(i, next)}
            onRemove={() => remove(i)}
          />
        ))}
        {list.length === 0 && (
          <div className="text-sm text-muted-foreground italic border border-dashed border-border rounded p-6 text-center">
            Nessuna attrezzatura ancora analizzata. Aggiungi alla coda e lancia l'analisi batch.
          </div>
        )}
      </div>
    </div>
  );
}
